<script setup lang="ts">
import type { BookingStop, StopNumber } from '@/models/advancedJobBooking';

import { computed } from 'vue';

import { buildStopsTimeline } from '@/helpers/stopsTimeline';

export interface Props {
  stops?: BookingStop[];
}

const props = withDefaults(defineProps<Props>(), {
  stops: () => [],
});

const open = defineModel<boolean>({ default: false });

const timeline = computed(() => buildStopsTimeline(props.stops));

function blockedFor(stopNumber: StopNumber): { widthPct: number }[] {
  return timeline.value.blocked.filter((item) => item.number === stopNumber);
}

function gridLeft(index: number): string {
  const count = timeline.value.ticks.length;
  if (count <= 1) return '0%';
  return `${(index / (count - 1)) * 100}%`;
}
</script>

<template>
  <v-dialog v-model="open" max-width="1100">
    <div
      :class="$style.stopTimeWindowTimelineDialog"
      data-testid="stopTimeWindowTimelineDialog"
    >
      <header :class="$style.header">
        <h2 :class="$style.title">Time window</h2>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="open = false"
        />
      </header>

      <div :class="$style.banner">
        <v-icon icon="mdi-information-outline" size="16" />
        <span>Available time will be 30 min after previous stops</span>
      </div>

      <div :class="$style.timelineCard">
        <div :class="$style.timelineTitle">
          Timeline ({{ timeline.rangeLabel }})
        </div>

        <div :class="$style.ticks">
          <span v-for="tick in timeline.ticks" :key="tick" :class="$style.tick">
            {{ tick }}
          </span>
        </div>

        <div :class="$style.tracks">
          <div :class="$style.gridlines">
            <span
              v-for="(tick, tickIndex) in timeline.ticks"
              :key="`grid-${tick}`"
              :class="$style.gridline"
              :style="{ left: gridLeft(tickIndex) }"
            />
          </div>
          <div
            v-for="bar in timeline.bars"
            :key="bar.number"
            :class="$style.track"
          >
            <div
              v-for="(block, blockIndex) in blockedFor(bar.number)"
              :key="`blocked-${bar.number}-${blockIndex}`"
              :class="[$style.bar, $style.barBlocked]"
              :style="{ left: '0%', width: `${block.widthPct}%` }"
            >
              <v-icon icon="mdi-map-marker-outline" size="14" />
              <span>Blocked</span>
            </div>
            <div
              :class="[$style.bar, $style[`barTier${bar.number}`]]"
              :style="{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }"
            >
              <v-icon icon="mdi-map-marker-outline" size="14" />
              <span>{{ bar.label }}</span>
            </div>
          </div>
        </div>

        <div :class="$style.legend">
          <span
            v-for="bar in timeline.bars"
            :key="`legend-${bar.number}`"
            :class="$style.legendItem"
          >
            <span :class="[$style.dot, $style[`barTier${bar.number}`]]" />
            {{ bar.label }} ({{ bar.role }})
          </span>
          <span :class="$style.legendItem">
            <span :class="[$style.dot, $style.barBlocked]" />
            Blocked
          </span>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.stopTimeWindowTimelineDialog {
  background: colors.$color-white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: colors.$color-text-primary;
  margin: 0;
}

.banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: colors.$color-gray-50;
  font-size: 13px;
  color: colors.$color-text-secondary;
}

.timelineCard {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: colors.$color-gray-50;
}

.timelineTitle {
  font-size: 14px;
  font-weight: 600;
  color: colors.$color-text-primary;
}

.ticks {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: colors.$color-text-tertiary;
}

.tracks {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gridlines {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.gridline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed colors.$color-gray-300;
}

.track {
  position: relative;
  height: 44px;
}

.bar {
  position: absolute;
  top: 0;
  height: 44px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  color: colors.$color-white;
  white-space: nowrap;
  overflow: hidden;
}

.barTier1 {
  background: bp.$color-brand-800;
}

.barTier2 {
  background: bp.$color-brand-500;
}

.barTier3 {
  background: bp.$color-brand-300;
}

.barBlocked {
  background: colors.$color-gray-300;
  color: colors.$color-text-secondary;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: colors.$color-text-secondary;
}

.legendItem {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
